import type { LeafPrimitive, SymbolComponent } from '@tsagko/label-contracts'
import { computeGtinCheckDigit } from '@tsagko/label-contracts'
import { useTranslation } from 'react-i18next'
import { checkDigitHint, type HintDescriptor } from './symbol-config'
import { ValueField } from './ValueField'
import { Gs1AiEditor } from './Gs1AiEditor'

interface SymbolSourceEditorProps {
  selected: SymbolComponent
  onChangePrimitive: (next: LeafPrimitive) => void
}

/**
 * Read-only hint for a GTIN/EAN-13 base value. Returns null for a {variable}
 * content or empty input. The printer/renderer computes the actual check digit;
 * this only previews/validates what the operator typed.
 *
 * Returns a {key, params} descriptor, not finished prose — a free function has
 * no `t`, so the component translates it at render. The short-input variant is
 * pluralised (`count`) instead of hardcoding an English "digits".
 */
function ean13Hint(content: string): HintDescriptor | null {
  const digits = content.replace(/\D/g, '')
  if (content.includes('{') || digits.length === 0) return null
  if (digits.length < 12) {
    return { key: 'symbology.hint.leftPad', params: { count: digits.length } }
  }
  if (digits.length === 12) {
    return {
      key: 'symbology.hint.printsAs',
      params: { digits, check: computeGtinCheckDigit(digits) },
    }
  }
  return checkDigitHint(digits.slice(0, 13))
}

/**
 * Symbology-aware Source editor for a symbol primitive. Switches on the data
 * structure kind:
 *  - raw/url → a single ValueField; QR additionally gets a "Treat as URL" toggle
 *    that flips kind raw↔url while preserving the value.
 *  - gtin → a ValueField plus a read-only check-digit hint.
 *  - gs1-ai → a repeatable AI-row editor (`<Gs1AiEditor>`).
 *
 * Capturing `const ds = selected.dataStructure` after the kind check lets TS
 * keep the narrowing inside the event-handler closures (no IIFE/`any` needed).
 */
export function SymbolSourceEditor({ selected, onChangePrimitive }: SymbolSourceEditorProps) {
  const { t } = useTranslation()
  const ds = selected.dataStructure

  if (ds.kind === 'raw' || ds.kind === 'url') {
    return (
      <>
        <ValueField
          content={ds.value.content}
          suggestedName={ds.kind === 'url' ? 'url' : 'value'}
          onChange={content =>
            onChangePrimitive({
              ...selected,
              dataStructure: { kind: ds.kind, value: { content } },
            })
          }
        />
        {selected.symbology === 'qr' && (
          <label className="mt-2 flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              data-testid="rpp-qr-url"
              checked={ds.kind === 'url'}
              onChange={e =>
                onChangePrimitive({
                  ...selected,
                  dataStructure: {
                    kind: e.target.checked ? 'url' : 'raw',
                    value: ds.value,
                  },
                })
              }
            />
            {t('symbology.treatAsUrl')}
          </label>
        )}
      </>
    )
  }

  if (ds.kind === 'gtin') {
    const hint = ean13Hint(ds.value.content)
    return (
      <>
        <ValueField
          content={ds.value.content}
          suggestedName="gtin"
          placeholder={t('symbology.gtinPlaceholder')}
          onChange={content =>
            onChangePrimitive({
              ...selected,
              dataStructure: { ...ds, value: { content } },
            })
          }
        />
        {hint && (
          <p data-testid="rpp-ean-hint" className="mt-1 text-[11px] text-stone-500">
            {t(hint.key, hint.params)}
          </p>
        )}
      </>
    )
  }

  if (ds.kind === 'gs1-ai') {
    return (
      <Gs1AiEditor
        elements={ds.elements}
        onChange={elements =>
          onChangePrimitive({ ...selected, dataStructure: { kind: 'gs1-ai', elements } })
        }
      />
    )
  }

  return null
}
